// src/App.js
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import PageTrackingWrapper from './posthog/PageTrackingWrapper';
import { useIdleTracker } from './posthog/hooks/useIdleTracker';

// Core Components & Pages
import AuthPage from './components/Auth/AuthPage';
import DashboardLayout from './components/Layout/DashboardLayout';
import DashboardOverview from './pages/DashboardOverview';
import ProtectedRoute from './components/Layout/ProtectedRoute';
import ClosedAccountGuard from './components/Layout/ClosedAccountGuard';
import ProfilePage from './pages/ProfilePage';
import HelpPage from './pages/HelpPage';
import SingleListingGeneratorPage from './pages/SingleListingGenerator';
import BulkListingGeneratorPage from './pages/BulkListingGenerator';
import ReferralPage from './pages/Referral';
import OnboardingFlow from './pages/OnboardingFlow';
import ManagePage from './pages/Manage';
import AmazonSubmissionsPage from './pages/MarketPlaces/AmazonSubmissionsPage';
import MarketPlacePage from './pages/MarketPlaces';
import CataloguePage from './pages/MarketPlaces/CataloguePage';
import CatalogueHealth from './pages/SellerIntelligence/CatalogueHealth/CatalogueHealth';
import FixesPage from './pages/SellerIntelligence/CatalogueHealth/components/FixesPage';
// --- Import FeedbackPage ---
import FeedbackPage from './pages/FeedbackPage';
// --- Import ListingScore Component ---
import ListingScore from './components/ListingScore/ListingScore';
// --- Import BulkListingScore Component ---
import BulkListingScore from './components/ListingScore/bulk_listing_score/BulkListingScore';
// --- Import ListingScoreResultHistory Component ---
import ListingScoreResultHistory from './components/ListingScore/bulk_listing_score/ListingScoreResultHistory';
import ListingScoreDetailPage from './pages/ListingScoreDetail/ListingScoreDetailPage';
// --- Import Tools Components ---
import CheckDealTags from './pages/Tools/CheckDealTags';
import ImageZipUploader from './pages/Tools/ImageZipUploader';
// Import SessionExpiredModal
import SessionExpiredModal from './components/Auth/SessionExpiredModal';
// Import Notification System
import { NotificationProvider } from './context/NotificationContext';
import GlobalNotification from './components/UI/GlobalNotification';
// Import ReferralHandler
import ReferralHandler from './components/ReferralHandler';
// Import FormStepProvider
import { FormStepProvider } from './context/FormStepContext';
import { AmazonSyncProvider } from './context/AmazonSyncContext';

// Placeholder imports for other pages/components (uncomment and use actual components when ready)
// import SettingsPage from './pages/SettingsPage';
import MySubscription from './pages/Plans/MySubscription';
import UsageHistoryPage from './pages/Plans/UsageHistoryPage';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './App.css'; // Global styles

const queryClient = new QueryClient();

// Landing site lives on a separate domain — anyone landing on legacy
// in-app routes (/home, /about, etc.) should be sent there instead.
const LANDING_HOST = process.env.REACT_APP_LANDING_URL || '';
const ExternalRedirect = () => {
  const location = useLocation();
  useEffect(() => {
    if (!LANDING_HOST) return;
    window.location.replace(`${LANDING_HOST}${location.pathname}${location.search}`);
  }, [location.pathname, location.search]);
  return null;
};

function App() {
  useIdleTracker();
  return (
    <QueryClientProvider client={queryClient}>
    <NotificationProvider>
      {/* Session Expired Modal - shown conditionally based on AuthContext state */}
      <SessionExpiredModal />

      {/* Global Notification System */}
      <GlobalNotification />

      <Router>
        <PageTrackingWrapper />
        <Routes>
          {/* --- Auth Page Route --- */}
          <Route path="/auth" element={<AuthPage />} />
          {/* --- Root --- */}
          <Route path="/" element={<Navigate replace to="/auth" />} />
          {/* --- Legacy landing routes — redirect to marketing site --- */}
          <Route path="/home" element={<ExternalRedirect />} />
          <Route path="/about" element={<ExternalRedirect />} />
          <Route path="/contact" element={<ExternalRedirect />} />
          <Route path="/pricing" element={<ExternalRedirect />} />
          <Route path="/solutions" element={<ExternalRedirect />} />
          <Route path="/blogs" element={<ExternalRedirect />} />
          <Route path="/blogs/:slug" element={<ExternalRedirect />} />
          <Route path="/thank-you" element={<ExternalRedirect />} />
          {/* --- Protected Dashboard Routes --- */}
          <Route element={<ProtectedRoute />}>
            <Route path="/onboarding" element={<OnboardingFlow />} />
          </Route>
          <Route element={<ProtectedRoute requireQuestionnaire />}> {/* This wrapper protects all nested routes */}
            <Route path="/dashboard" element={
              <FormStepProvider>
                <AmazonSyncProvider>
                  <DashboardLayout />
                </AmazonSyncProvider>
              </FormStepProvider>
            }>
              {/* Index route for the dashboard */}
              <Route index element={<DashboardOverview />} />

              {/* --- Generator Routes --- */}
              <Route path="generate/single" element={<ClosedAccountGuard><SingleListingGeneratorPage /></ClosedAccountGuard>} />
              <Route path="generate/bulk" element={<ClosedAccountGuard><BulkListingGeneratorPage /></ClosedAccountGuard>} />

              {/* --- Listing Score Routes --- */}
              <Route path="listing-score" element={<ListingScore />} />
              <Route path="bulk-listing-score" element={<BulkListingScore />} />
              <Route path="listing-score-results" element={<ListingScoreResultHistory />} />
              <Route path="listing-score-detail" element={<ListingScoreDetailPage />} />
              <Route path="market-places" element={<MarketPlacePage />} />
              <Route path="catalogue" element={<CataloguePage />} />
              <Route path="seller-intelligence/catalogue-health" element={<CatalogueHealth />} />
              <Route path="seller-intelligence/fixes" element={<FixesPage />} />

              {/* --- Tools Routes (deal tag checker temporarily disabled) --- */}
              <Route path="tools/check-deal-tags" element={<CheckDealTags />} />
              <Route path="tools/image-zip-upload" element={<ImageZipUploader />} />

              {/* Other Dashboard Sections */}
              <Route path="projects" element={<ManagePage />} />
              <Route path="projects/:project_id" element={<ManagePage />} />
              <Route path="amazon-submissions" element={<AmazonSubmissionsPage />} />
              <Route path="feedback" element={<FeedbackPage />} />
              <Route path="referral" element={<ReferralPage />} />
              <Route path="help" element={<HelpPage />} />
              <Route path="settings" element={<div><h1>Settings</h1>{/* <SettingsPage /> */}</div>} />
              <Route path="profile" element={<ProfilePage />} />
              <Route path="plans" element={<MySubscription />} />
              <Route path="usage-history" element={<UsageHistoryPage />} />

              {/* Catch-all for non-existent dashboard routes */}
              <Route path="*" element={<div><h2>404 - Dashboard Page Not Found</h2><p>Oops! The page you're looking for inside the dashboard doesn't exist.</p></div>} />
            </Route>
          </Route>

          {/* --- Global Catch-all --- */}
          {/* Check if it's a referral code, otherwise redirect to home */}
          <Route path="*" element={<ReferralHandler />} />
        </Routes>
      </Router>
    </NotificationProvider>
    </QueryClientProvider>
  );
}

export default App;